package org.braunequip.web.util

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.braunequip.web.components.models.*
import org.w3c.fetch.*

/*
     -------------------------------------

           MAINT_SCHED FUNCTIONS

     -------------------------------------
*/
suspend fun createMaintenanceSched(
    MaintenanceSchedDTO: MaintSchedulesDTO,
    onFinish: (String) -> Unit
) {
    val jsonSerializer = Json { encodeDefaults = true }
    val jsonData = jsonSerializer.encodeToString(MaintenanceSchedDTO)

    val response: Response? = try {
        window.fetch(
            input = "${BackendRoutes.MAINT_SCHED}/new",
            RequestInit(
                method = "POST",
                headers = Headers().apply {
                    append("Content-Type", "application/json")
                },
                body = jsonData,
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error: $e")
        null
    }

    if (response?.ok == true) {
        onFinish("Submitted!")
    } else {
        onFinish("Error, try again later!")
    }
}

suspend fun fetchMaintenanceSchedules(
    skip: Int,
    all: Boolean = false,
    onSuccess: (List<MaintSchedulesDTO>) -> Unit,
    onError: (String) -> Unit
) {
    val response: Response? = try {
        window.fetch(
            input = "${BackendRoutes.MAINT_SCHED}?skip=$skip&all=$all",
            RequestInit(
                method = "GET",
                headers = Headers().apply {
                    append("Accept", "application/json")
                    append("Content-Type", "application/json")
                },
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error Fetching MAINT_SCHED!: $e")
        null
    }

    if (response != null) {
        val pinsResponseText = response.text().await()
        val MAINT_SCHEDList = Json.decodeFromString<List<MaintSchedulesDTO>>(pinsResponseText)
        onSuccess(MAINT_SCHEDList)
    } else {
        onError("Error fetching pins!")
    }
}

suspend fun updateMaintenanceSched(
    MaintenanceSched: MaintSchedulesDTO
): Boolean {
    val jsonData = jsonSerializer.encodeToString(MaintenanceSched)

    val response: Response? = try {
        window.fetch(
            input = BackendRoutes.MAINT_SCHED,
            RequestInit(
                method = "PUT",
                headers = Headers().apply {
                    append("Content-Type", "application/json")
                },
                body = jsonData,
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error: $e")
        null
    }

    return response?.ok == true
}

suspend fun deleteSelectedMaintenanceSched(
    ids: List<String>
): Boolean {
    return try {
        val response = window.fetch(
            input = "${BackendRoutes.MAINT_SCHED}/delete",
            RequestInit(
                method = "POST",
                headers = Headers().apply {
                    append("Accept", "application/json")
                    append("Content-Type", "application/json")
                },
                credentials = RequestCredentials.INCLUDE,
                body = Json.encodeToString(ids)
            )
        ).await()

        response.ok
    } catch (e: Exception) {
        console.log(e)
        false
    }
}

/*
     -------------------------------------

           MAINTENANCE ITEMS FUNCTIONS

     -------------------------------------
*/
suspend fun submitScheduleForItems(
    request: NewMaintItemRequest,
    onFinish: (String, NewMaintItemResponse) -> Unit
) {
    val jsonSerializer = Json { encodeDefaults = true }
    val jsonData = jsonSerializer.encodeToString(request)

    val response: Response? = try {
        window.fetch(
            input = "${BackendRoutes.MAINT_ITEMS}/new", // or whatever route you're using
            RequestInit(
                method = "POST",
                headers = Headers().apply {
                    append("Content-Type", "application/json")
                },
                body = jsonData,
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error submitting maintenance items: $e")
        null
    }

    if (response?.ok == true) {
        val respText = response.text().await()
        onFinish(
            "Success! $respText",
            Json.decodeFromString<NewMaintItemResponse>(respText)
        )
    } else {
        val errText = response?.text()?.await() ?: "Unknown error"
        console.log(errText)
        onFinish(
            "Failed: $errText",
            NewMaintItemResponse(
                createdCount = 0,
                skippedAssets = emptyList()
            )
        )
    }
}

suspend fun fetchAssetMaintenanceItemsByID(
    assetID: String,
    status: Short? = null,
    skip: Int,
    onSuccess: (List<MaintItemsDTO>) -> Unit,
    onError: (String) -> Unit
) {
    var url = "${BackendRoutes.MAINT_ITEMS}?skip=$skip&assetId=$assetID"
    status?.let {
        url += "&status=$status"
    }

    val response: Response? = try {
        window.fetch(
            input = url,
            RequestInit(
                method = "GET",
                headers = Headers().apply {
                    append("Accept", "application/json")
                    append("Content-Type", "application/json")
                },
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error Fetching Maint Items for $assetID!: $e")
        null
    }

    if (response != null) {
        val responseText = response.text().await()
        val maintenanceItemsList = Json.decodeFromString<List<MaintItemsDTO>>(responseText)
        onSuccess(maintenanceItemsList)
    } else {
        onError("Error fetching Maintenance Items for ID: $assetID!")
    }
}

suspend fun updateCompleteMaintenanceItem(
    completedItem: CompleteMaintItemRequest
): Boolean {

    val response: Response? = try {
        window.fetch(
            input = "${BackendRoutes.MAINT_ITEMS}/complete",
            RequestInit(
                method = "POST",
                headers = Headers().apply {
                    append("Content-Type", "application/json")
                },
                body = jsonSerializer.encodeToString(completedItem),
                credentials = RequestCredentials.INCLUDE
            )
        ).await()
    } catch (e: Exception) {
        console.log("Error: $e")
        null
    }

    return response?.ok == true
}

suspend fun deleteSelectedMaintenanceItems(
    id: Int
): Boolean {
    return try {
        val response = window.fetch(
            input = "${BackendRoutes.MAINT_ITEMS}/delete",
            RequestInit(
                method = "POST",
                headers = Headers().apply {
                    append("Accept", "application/json")
                    append("Content-Type", "application/json")
                },
                credentials = RequestCredentials.INCLUDE,
                body = Json.encodeToString(id)
            )
        ).await()

        response.ok
    } catch (e: Exception) {
        console.log(e)
        false
    }
}

